import { ArrowForward, Favorite as HeartIcon } from "@mui/icons-material";
import { Box, Typography, Card, CardContent, IconButton } from "@mui/material";
import React, { ReactNode } from "react";

import { getMetricConfigByName } from "../../../../constants/metrics";
import { useCelleraTheme } from "../../../../theme/useCelleraTheme";

interface MetricData {
  id: string;
  name: string;
  value?: string;
  unit?: string;
  startEndDate?: {
    startDate: string;
    endDate: string;
  };
  lastChecked: string;
  hasData: boolean;
  fallbackInfo?: {
    message: string;
    suggestion: string;
    lastRecorded?: string;
  };
}

interface MetricCardProps {
  metric: MetricData;
  customValue?: ReactNode;
  onNext?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  metric,
  customValue,
  onNext,
}) => {
  const { colors } = useCelleraTheme();

  // Get metric icon from centralized configuration
  const getMetricIcon = (metricName: string) => {
    const iconProps = { sx: { color: colors.primary.main, fontSize: 20 } };
    const metricConfig = getMetricConfigByName(metricName);

    if (metricConfig?.icon) {
      const IconComponent = metricConfig.icon;
      return <IconComponent {...iconProps} />;
    }

    return <HeartIcon {...iconProps} />;
  };

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: colors.border.light,
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flex: 1, p: 2 }}>
        {/* Header with icon and title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {getMetricIcon(metric.name)}
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              {metric.name}
            </Typography>
          </Box>
          {onNext && (
            <IconButton
              disabled={!metric.hasData}
              onClick={onNext}
              sx={{ color: "text.secondary" }}
              size="small"
            >
              <ArrowForward />
            </IconButton>
          )}
        </Box>

        {/* Separator line */}
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: colors.border.light,
            mb: 2,
          }}
        />

        {customValue && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              {customValue}
            </Typography>
          </Box>
        )}

        {metric.startEndDate && !customValue && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              {metric.startEndDate.startDate}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              {metric.startEndDate.endDate}
            </Typography>
          </Box>
        )}

        {/* Main value and unit */}
        {metric.value && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              {metric.hasData
                ? `${metric.value}` + (metric.unit ? ` ${metric.unit}` : "")
                : "No data"}
            </Typography>
          </Box>
        )}

        {/* Last checked timestamp */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            display: "block",
            fontSize: "0.875rem",
          }}
        >
          {metric.hasData
            ? `Last checked ${metric.lastChecked}`
            : metric.fallbackInfo?.message || "No data available"}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
export type { MetricData };
